import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation, useNavigate} from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel } from "swiper/modules";
import "swiper/css";
import axiosInstance from "../api/axiosInstance";
import { Map, Star, StarBorder, SellRounded, IosShare, FavoriteRounded, FavoriteBorderRounded, BookmarkRounded, BookmarkBorderRounded, Place, DepartureBoard } from "@mui/icons-material";
import StyleIcon from '@mui/icons-material/Style';
import IconButton from "@mui/material/IconButton";
import { CircularProgress } from '@mui/material';
import Comments from "./Comments"
import { universalShare } from "../utils/shareUtils";
import "../styles/styles.css"
import { MoreVert } from "@mui/icons-material";
import { Popover, List, ListItem, Typography } from "@mui/material";
import TagDialog from '../components/TagDialog';
import '../styles/trippin.css';
import ReportDialog from '../components/ReportDialog';
import { isVideoFile } from '../utils/mediaUtils';

export default function TrippinPostView({
  posts: postsProp,         
  selectedPostId: selectedPostIdProp,  
  enableRouting = true,
}) {
  const { trippinId, postId } = useParams(); 
  const routePostId = trippinId || postId;
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [reportEntityType, setReportEntityType] = useState(null);
  const [reportEntityId, setReportEntityId] = useState(null);
  const location = useLocation();
  const [isTrippinLiked, setIsTrippinLiked] = useState(false);
  const [isTrippinSaved, setIsTrippinSaved] = useState(false);
  const postsFromState = !postsProp ? location.state?.posts || [] : postsProp;
  const selectedPostId = selectedPostIdProp ? selectedPostIdProp : location.state?.selectedPostId || parseInt(routePostId, 10);
  // const profilePicture = location.state?.profilePicture || null;
  // const username = location.state?.username || null;
  const [username, setUsername] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const isOwnerFromState = location.state?.isOwner || false;
  const isProfileFromState = location.state?.isProfile || false;
  const isMyTrippinView = location.state?.isMyTrippinView || false;
  const [wholeTripTags, setWholeTripTags] = useState([]);
  const selectedTrippinId = location.state?.selectedTrippinId; 
  const [loading, setLoading] = useState(true);
  const [allPlaces, setAllPlaces] = useState([]);
  const [initialPostIndex, setInitialPostIndex] = useState(0);
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const allowTagging = location.state?.allowTagging || false; 
  const [isPlaceTagDialogOpen, setIsPlaceTagDialogOpen] = useState(false);
  const [selectedPlaceIdForTagging, setSelectedPlaceIdForTagging] = useState(null);
  const [placeTags, setPlaceTags] = useState({});

  const outerSwiperRef = useRef(null);
  const activeVideoRef = useRef(null);

  const handleVerticalSlideChange = (swiper) => {
    if (activeVideoRef.current) {
      activeVideoRef.current.pause();
      activeVideoRef.current.currentTime = 0;
      activeVideoRef.current = null;
    }
    const activeVerticalSlide = swiper.slides[swiper.activeIndex];
    if (activeVerticalSlide) {
      const horizontalSwiperContainer = activeVerticalSlide.querySelector(".swiper-horizontal");
      if (horizontalSwiperContainer && horizontalSwiperContainer.swiper) {
        const horizontalActiveIndex = horizontalSwiperContainer.swiper.activeIndex;
        const activeHorizontalSlide = horizontalSwiperContainer.swiper.slides[horizontalActiveIndex];
        if (activeHorizontalSlide) {
          const videoElement = activeHorizontalSlide.querySelector("video");
          if (videoElement) {
            activeVideoRef.current = videoElement;
            videoElement.play().catch((err) =>
              console.error("Error autoplaying video:", err)
            );
          }
        }
      }
    }

    if (swiper.activeIndex === swiper.slides.length - 1) {
      requestAnimationFrame(() => outerSwiperRef.current.swiper.slideNext());
    }
  };
  
  const handleConfirmTagPlace = async (userIds, selectedUsers) => {
    if (!userIds || !selectedPlaceIdForTagging) return;
    try {
      await axiosInstance.put(`/mytrippins/${selectedTrippinId}/tagPlace/${selectedPlaceIdForTagging}`, {
        userIds,
      });
  
      alert('Place tags updated!');
      setPlaceTags((prevTags) => ({
        ...prevTags,
        [selectedPlaceIdForTagging]: selectedUsers,
      }));
    } catch (err) {
      console.error(err);
      alert('Failed to update place tags');
    } finally {
      setIsPlaceTagDialogOpen(false);
    }
  };
  
  const handleConfirmTagTrippin = async (userIds, selectedUsers) => {
    if (!userIds) return;
    try {
      await axiosInstance.put(`/mytrippins/${selectedTrippinId}/tagTrippin`, {
        userIds,
      });
      alert('Trippin tags updated!');
      setWholeTripTags(selectedUsers);
    } catch (err) {
      console.error(err);
      alert('Failed to update trippin tags');
    } finally {
      setIsTagDialogOpen(false);
    }
  };

  useEffect(() => {
    const handleSlideChange = (swiper) => {
      const activeIndex = swiper.activeIndex;
      const newPost = postsFromState[activeIndex];
  
      setIsTrippinLiked(newPost?.isLiked || false);
      setIsTrippinSaved(newPost?.isSaved || false);
    };
  
    if(outerSwiperRef.current) {
      const swiperInstance = outerSwiperRef.current.swiper;
      swiperInstance.on('slideChange', () => handleSlideChange(swiperInstance));
    }
  
    return () => {
      if (outerSwiperRef.current?.swiper) {
        const swiperInstance = outerSwiperRef.current.swiper;
        swiperInstance.off('slideChange', handleSlideChange);
      }
    };
  }, [postsFromState]);

  const fetchSinglePostOnly = async (id, viewType = "mytrippins") => {
    try {
      let endpoint = viewType === "mytrippins" 
        ? `/mytrippins/${id}` 
        : `/mytrippins/tagged/${id}`;
        console.log(`Fetching data from endpoint: ${endpoint}`);

      const response = await axiosInstance.get(endpoint);

      console.log("Fetched data:", response.data);

      const groupPlacesByName = (places) => {
        if (!Array.isArray(places)) return [];
        console.log("Raw places:", places);

        const grouped = {};
        places.forEach((place) => {
          if (!grouped[place.place_name]) {
            grouped[place.place_name] = {
              ...place,
              images: Array.isArray(place.signed_url) ? place.signed_url : [place.signed_url],
            };
          }
          else {
            grouped[place.place_name].images.push(
              ...(Array.isArray(place.signed_url) ? place.signed_url : [place.signed_url])
            );
          }
        });

        const result = Object.values(grouped);
        console.log("Grouped places:", result);
        return result;
      };

      const places = groupPlacesByName(response.data.places || []);
      console.log("Grouped Places Before State Update:", places);

      setAllPlaces(Array.isArray(places) ? places : []);
      setIsTrippinLiked(response.data?.isLiked || false);
      setIsTrippinSaved(response.data?.isSaved || false);
    } catch (error) {
      console.error("Error fetching single post detail:", error);
      alert("Failed to fetch Trippin post. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("useEffect triggered:", { postsFromState, routePostId, enableRouting });
    console.log("useEffect triggered:", {
      postsFromState,
      routePostId,
      enableRouting,
      locationState: location.state,
    });
    if(!postsFromState || postsFromState.length === 0) {
      if (enableRouting && routePostId) {
        const viewType = location.state?.isMyTrippin ? "mytrippins" : "taggedtrippins";
        fetchSinglePostOnly(routePostId, viewType);
      }
      else {
        setLoading(false);
      }
      return;
    }
    
    console.log("PostsFromState exists, skipping fetchSinglePostOnly.");

    const postIndex = postsFromState.findIndex(
      (post) => post.trippin_post_id === selectedPostId
    );
    const selectedIndex = postIndex === -1 ? 0 : postIndex;
    setIsTrippinLiked(postsFromState[selectedIndex]?.isLiked || false);
    setIsTrippinSaved(postsFromState[selectedIndex]?.isSaved || false);
    setInitialPostIndex(selectedIndex);

    if (!enableRouting) {
      const groupedPlacesData = postsFromState.map((post) => {
        if (!post.places) return [];
        
        const grouped = {};
        post.places.forEach((place) => {
          if (!grouped[place.place_name]) {
            grouped[place.place_name] = {
              ...place,
              images: Array.isArray(place.content_url) ? place.content_url : [place.content_url],
              post_place_ids: [place.post_place_id], // Initialize with the current post_place_id
              isSaved: place.isSaved || false, // Initialize isSaved state
            };
          }
          else {
            grouped[place.place_name].images.push(
              ...(Array.isArray(place.content_url) ? place.content_url : [place.content_url])
            );
            grouped[place.place_name].post_place_ids.push(place.post_place_id); // Add additional post_place_id
          }
        });
        return Object.values(grouped);
      });      
      setAllPlaces(groupedPlacesData);
      setLoading(false);
      return;
    }
    Promise.all(
      postsFromState.map(async (post) => {
        const resp = await axiosInstance.get(`/trippinById/${post.trippin_post_id}/detail`);
        if (post.trippin_post_id === selectedPostId) {
          setIsTrippinLiked(resp.data?.isLiked);
          setIsTrippinSaved(resp.data?.isSaved);
        }  
        setProfilePicture(resp.data?.userInfo.profile_url);
        setUsername(resp.data?.userInfo.username);
        return resp.data?.places || [];
      })
    )
      .then((placesArrays) => {
        setAllPlaces(placesArrays);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching places for posts:", error);
        setLoading(false);
      });
  }, [selectedPostId, routePostId, enableRouting]);

  if (loading) {
    return (
      <div className="loading-container">
        <CircularProgress size={40} sx={{ color: '#888' }} />
      </div>
    );
  }

  if (!allPlaces.length || allPlaces.every((p) => p.length === 0)) {
    return (
      <div className="loading-container" style={{ height: "unset" }}>
        <p style={{ fontSize: "18px", color: "#555", textAlign: "center", marginTop: "50px" }}>
          No places found        
        </p>
      </div>
    );
  }

  if (!allPlaces || allPlaces.length === 0) {
    return (
      <div className="loading-container">
        <CircularProgress size={40} sx={{ color: '#888' }} />
      </div>
    );
  }

  const openReportDialog = (entityType, entityId) => {
    setReportEntityType(entityType);
    setReportEntityId(entityId);
    setIsReportDialogOpen(true);
  };

  const closeReportDialog = () => {
    setIsReportDialogOpen(false);
    setReportEntityType(null);
    setReportEntityId(null);
  };

  return (
    <div style={{ position: "relative", width: "100vw", height: "100vh" }}>
       <ReportDialog
        open={isReportDialogOpen}
        onClose={closeReportDialog}
        reportedEntityId={reportEntityId}
        entityType={reportEntityType}
      />
      <Swiper
        ref={outerSwiperRef}
        key={initialPostIndex}
        direction="vertical"
        touchEventsTarget="container"
        modules={[Mousewheel]}
        mousewheel={false}
        initialSlide={initialPostIndex}
        style={{ height: "100vh" }}
        onSlideChange={handleVerticalSlideChange}
      >
        {allPlaces.map((placesForThisPost, postIdx) => (
          <SwiperSlide
            key={
              postsFromState[postIdx]?.trippin_post_id ||
              `index-${postIdx}`
            }
          >
            {allowTagging && (    
              <div style={{ marginTop: "40px" }}>
                <button className="tag-button" onClick={() => setIsTagDialogOpen(true)}>
                    Tag entire trippin
                </button>
                {isTagDialogOpen && (
                  <TagDialog
                    open={isTagDialogOpen}
                    onClose={() => setIsTagDialogOpen(false)}
                    onConfirm={(userIds, selectedUsers) => handleConfirmTagTrippin(userIds, selectedUsers)}
                    existingTags={wholeTripTags}
                  />
                )}
              </div>
            )}
            
            {allowTagging && (    
              <>
                {isPlaceTagDialogOpen && (
                  <TagDialog
                    open={isPlaceTagDialogOpen}
                    onClose={() => setIsPlaceTagDialogOpen(false)}
                    onConfirm={(userIds, selectedUsers) => handleConfirmTagPlace(userIds, selectedUsers)}
                    existingTags={placeTags[selectedPlaceIdForTagging] || []}
                  />
                )}
              </>
            )}

            <SinglePostView
              places={placesForThisPost || []}
              outerSwiper={outerSwiperRef.current?.swiper}
              activeVideoRef={activeVideoRef}
              trippinPost={{
                ...postsFromState[postIdx],
                isLiked: postIdx === initialPostIndex ? isTrippinLiked : postsFromState[postIdx]?.isLiked,
                isSaved: postIdx === initialPostIndex ? isTrippinSaved : postsFromState[postIdx]?.isSaved,
                countries: postsFromState[postIdx]?.countries,
              }}
              profilePicture={profilePicture}
              username={username}
              isOwner={isOwnerFromState}
              isProfile={isProfileFromState}
              isMyTrippinView={isMyTrippinView}
              allowTagging = {allowTagging}
              setSelectedPlaceIdForTagging={setSelectedPlaceIdForTagging}
              setIsPlaceTagDialogOpen={setIsPlaceTagDialogOpen}
              openReportDialog={openReportDialog}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

const SinglePostView = ({
  places,
  outerSwiper,
  activeVideoRef,
  trippinPost,
  profilePicture,
  username,
  isOwner,
  isProfile,
  isMyTrippinView,
  allowTagging,  // ✅ Receive allowTagging prop
  setSelectedPlaceIdForTagging,
  setIsPlaceTagDialogOpen,
  openReportDialog,
}) => { 
  const verticalSwiperRef = useRef(null);
  const navigate = useNavigate();
  const placesArray = Array.isArray(places) ? places : [places];

  const SWIPE_THRESHOLD = 50;
  //const [startY, setStartY] = useState(null);
  const startYRef = useRef(null);
  const [isTrippinLiked, setIsTrippinLiked] = useState(trippinPost?.isLiked || false);
  const [isTrippinSaved, setIsTrippinSaved] = useState(trippinPost?.isSaved || false);
  const [placesState, setPlacesState] = useState([]);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);

  const handleHorizontalSlideChange = (swiper) => {
    // When horizontal slide changes, pause any currently active video
    if (activeVideoRef.current) {
      activeVideoRef.current.pause();
      activeVideoRef.current.currentTime = 0;
      activeVideoRef.current = null;
    }
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (activeSlide) {
      const videoElement = activeSlide.querySelector("video");
      if (videoElement) {
        activeVideoRef.current = videoElement;
        videoElement.play().catch((err) =>
          console.error("Error autoplaying video:", err)
        );
      }
    }
  };

  useEffect(() => {
    if (places.length > 0) {
      setPlacesState(
        places.map((p) => ({
          isSaved: !!p.is_saved,  // Ensure `is_saved` from backend is correctly assigned
        }))
      );
    }
  }, [places]);  // ✅ Ensure placesState is updated when places change
  
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const toggleMenu = () => setShowMoreMenu((prev) => !prev);
  // Place near other React state hooks in SinglePostView
  const [anchorEl, setAnchorEl] = useState(null);
  const [taggedUsers, setTaggedUsers] = useState([]);

  const handleOpenPopover = (event, tagData) => {
    setAnchorEl(event.currentTarget);
    setTaggedUsers(tagData);   // `tagData` could be an array of user objects or just IDs
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setTaggedUsers([]);
  };

  const isPopoverOpen = Boolean(anchorEl);
  
  const sendEngagementMetric = async (engagementType, postPlaceId, trippinPostId, engagementValue = null) => {
    try {
      await axiosInstance.post("/engagement", {
        engagement_type: engagementType,  
        post_place_id: postPlaceId,      
        trippin_post_id: trippinPostId,  
        engagement_value: engagementValue,  
      });
    } catch (error) {
      console.error("Error logging engagement metric:", error);
    }
  };
  
  const startTrackingViewDuration = (postPlaceId, trippinPostId) => {
    let duration = 0;
    const interval = setInterval(() => {
      duration += 1;
      if (duration % 5 === 0) {
        sendEngagementMetric("view", postPlaceId, trippinPostId, duration);
      }
    }, 1000);
  
    return () => clearInterval(interval);
  };

  useEffect(() => {
    if (places.length > 0) {
      const stopTracking = startTrackingViewDuration(places[0].post_place_ids[0], trippinPost.trippin_post_id);
      return () => stopTracking();
    }
  }, [trippinPost.trippin_post_id]); // Track when post changes
  
  const deleteTrippin = async () => {
    if (!window.confirm('Are you sure you want to delete this Trippin?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/trippinById/${trippinPost.trippin_post_id}`);
      alert('Trippin deleted successfully');
      // Optionally navigate back to the profile or feed
      navigate(-1); // or navigate(`/profile/${trippinPost.user_id}`)
    } catch (err) {
      console.error('Error deleting trippin:', err);
      alert('Failed to delete trippin.');
    }
  };

  useEffect(() => {
    setIsTrippinLiked(trippinPost?.isLiked || false);
    setIsTrippinSaved(trippinPost?.isSaved || false);
    console.log(taggedUsers);
  }, [trippinPost]);

  const handleProfileClick = () => {
    if (trippinPost?.user_id) {
      navigate(`/profile/${trippinPost.user_id}`);
    }
  };

  const handleTouchStart = (e) => {
    startYRef.current = e.touches[0].clientY;
    //e.stopPropagation();
  };

  const handleTouchEnd = (e) => {
    if (startYRef.current === null) return;
  
    const deltaY = startYRef.current - e.changedTouches[0].clientY;
    if (deltaY > SWIPE_THRESHOLD) handleMouseOver("next");
    else if (deltaY < -SWIPE_THRESHOLD) handleMouseOver("prev");
  
    startYRef.current = null;
    //e.stopPropagation();
  };

  const handleMouseOver = (direction) => {
    if (!outerSwiper) return;

    if (direction === "next") {
      outerSwiper.slideNext();
    }
    else if (direction === "prev") {
      outerSwiper.slidePrev();
    }
  };
 
  const toggleTrippinLike = async () => {
    try {
      if (isTrippinLiked) {
        await axiosInstance.delete(`/trippins/${trippinPost.trippin_post_id}/like`);
        setIsTrippinLiked(false);
      }
      else {
        await axiosInstance.post(`/trippins/${trippinPost.trippin_post_id}/like`);
        setIsTrippinLiked(true);
      }
    } catch (error) {
      console.error("Error toggling trippin like:", error);
    }
  };

  const toggleTrippinSave = async () => {
    try {
      if (isTrippinSaved) {
        await axiosInstance.delete(`/trippins/${trippinPost.trippin_post_id}/save`);
        setIsTrippinSaved(false);
      }
      else {
        await axiosInstance.post(`/trippins/${trippinPost.trippin_post_id}/save`);
        setIsTrippinSaved(true);
      }
    } catch (error) {
      console.error("Error toggling trippin save:", error);
    }
  };

  const shareTrippin = async () => {
    const shareUrl = `https://trippin.icu/trippin/${trippinPost.trippin_post_id}`;
    try {
      await axiosInstance.post(`/trippins/${trippinPost.trippin_post_id}/share`);
      
      await universalShare({
        title: "Check out this trippin!",
        text: "",
        url: shareUrl,
      });
    } catch (error) {
      console.error("Error sharing trippin:", error);
    }
  };
  
  const openTrippinInMaps = () => {
    if (places.length < 2) {
      const url = `https://www.google.com/maps?q=${places[0].latitude},${places[0].longitude}`;
      window.open(url, "_blank");
    }
    else {
      const origin = `${places[0].latitude},${places[0].longitude}`;
      const destination = `${places[places.length - 1].latitude},${places[places.length - 1].longitude}`;

      const waypoints = places.slice(1, -1).map((place) => `${place.latitude},${place.longitude}`);

      const waypointsParam = waypoints.length > 0 ? `&waypoints=${waypoints.join('|')}` : '';

      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&waypoints=${waypointsParam}&travelmode=driving`;
      window.open(googleMapsUrl, '_blank');
    }
  };
     
  const togglePlaceSave = async (placeIndex, placeId, postPlaceIdsArray) => {
    const postPlaceId = postPlaceIdsArray?.[0];  

    if (!postPlaceId) {
      console.warn("❌ No valid post_place_id to save.");
      return;
    }

    try {
      const updatedPlaces = [...placesState];
      const isSaved = updatedPlaces[placeIndex]?.isSaved || false;
  
      if (isSaved) {
        await axiosInstance.delete(`/trippins/places/${placeId}/save`, {
          data: { post_place_id: postPlaceId },
        });
      }
      else {
        await axiosInstance.post(`/trippins/places/${placeId}/save`, {
          post_place_id: postPlaceId,
        });
      }
  
      updatedPlaces[placeIndex].isSaved = !isSaved;
      setPlacesState(updatedPlaces);
    } catch (error) {
      console.error("Error toggling place save:", error);
    }
  };
  
  const sharePlace = async (placeId) => {
    const shareUrl = `https://trippin.icu/trippin/${trippinPost.trippin_post_id}/places/${placeId}`;
    try {
      await axiosInstance.post(`/trippins/places/${placeId}/share`);
      await universalShare({
        title: "Check out this trippin place!",
        text: "",
        url: shareUrl,
      });
    } catch (error) {
      console.error("Error sharing place:", error);
    }
  };

  const openPlaceInMaps = (place) => {
    if (place.latitude && place.longitude) {
      const url = `https://www.google.com/maps?q=${place.latitude},${place.longitude}`;
      window.open(url, "_blank");
    }
    else {
      alert("No lat/lng found");
    }
  };

  const openPlaceComments = (postPlaceId) => {
    setSelectedPlaceId(postPlaceId);
    setIsCommentsOpen(true);
    document.body.style.overflow = "hidden";
    document.querySelector(".bottom-nav")?.classList.add("hidden");
  };

  const closePlaceComments = () => {
    setIsCommentsOpen(false);
    document.body.style.overflow = "";
    document.querySelector(".bottom-nav")?.classList.remove("hidden");
  };

  const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const formattedStartTime = (startTime) => {
    if (!startTime) return;

    const dateString = startTime;
    const date = new Date(dateString);
  
    const options = { year: "numeric", month: "long" };
    return date.toLocaleDateString("en-US", options).replace(" ", ", ");
  };

  return (
    <div className="trippin-container">
      {/* Vertical Swiper */}
      <Swiper
        ref={verticalSwiperRef}
        direction="vertical"
        modules={[Mousewheel]}
        mousewheel={false}
        touchEventsTarget="wrapper"
        passiveListeners={false}
        nested={true}
        className="vertical-swiper"
        noSwiping={true}
        noSwipingClass="swiper-no-swiping"
      >
        {placesArray.map((place, placeIndex) => (
          <SwiperSlide key={placeIndex}>
            <div className="place-slide">

              {!isMyTrippinView && (
                <div className="place-header-container">
                  <div className={`place-header ${isProfile ? "is-profile" : ""}`}>

                    {/* Profile Info */}
                    <div className="profile-container" onClick={handleProfileClick}>
                      <img
                        src={profilePicture || trippinPost.profile_url || "/assets/images/default_profile.png"}
                        alt="User Profile"
                        className="profile-picture"
                      />
                      <div className="profile-text">
                        <span className="profile-username">{username || trippinPost.username}</span>
                        <span className="start-time">{formattedStartTime(trippinPost.start_time)}</span>
                      </div>
                    </div>

                    {Array.isArray(place.tagged_users) &&
                      place.tagged_users.filter(Boolean).length > 0 && (
                        <span
                          className="tagged-user-badge"
                          onClick={(e) => handleOpenPopover(e, place.tagged_users)}
                        >
                          +{place.tagged_users.length}
                        </span>
                      )
                    }

                    <Popover
                      open={isPopoverOpen}
                      anchorEl={anchorEl}
                      onClose={handleClosePopover}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                      sx={{
                        "& .MuiPopover-paper": {
                          backgroundColor: "#f9f9f9",
                          boxShadow: "0 0 2px rgba(142, 142, 142, 0.1)",
                          borderRadius: "10px",
                          padding: 0,
                          minWidth: "180px",
                        },
                      }}
                    >
                      <List sx={{ padding: 0 }}>
                        {taggedUsers.map((tagUser, i) => (
                          <ListItem
                            key={i}
                            onClick={() => {
                              navigate(`/profile/${tagUser.user_id}`);
                              handleClosePopover();
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "#f1f1f1",
                              },
                            }}
                          >
                            <img
                              src={
                                tagUser.profile_url ||
                                "/assets/images/default_profile.png"
                              }
                              alt={tagUser.username}
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                              }}
                            />
                            <Typography variant="body2">
                              @{tagUser.username}
                            </Typography>
                          </ListItem>
                        ))}
                      </List>
                    </Popover>

                    {/* Duration Badge */}
                    {trippinPost.duration && (
                      <div className="duration-badge">
                        <DepartureBoard className="duration-icon" />
                        {typeof trippinPost.duration === "object"
                          ? trippinPost.duration.minutes
                          : trippinPost.duration}{" "}
                        min
                      </div>
                    )}
                    
                    {/* Flags */}
                    {Array.isArray(trippinPost.countries) && trippinPost.countries.length > 0 && (
                      <div className="flags-container">
                        {trippinPost.countries.map((iso) =>
                          typeof iso === "string" ? (
                            <img
                              key={iso}
                              src={`/assets/flags/${iso.toLowerCase()}.svg`}
                              alt={iso}
                              className="flag-image"
                            />
                          ) : null
                        )}
                      </div>
                    )}

                    <div className="owner-menu">
                      <IconButton onClick={toggleMenu} className="menu-button" style={{ padding: 0, color: "black" }}>
                        <MoreVert />
                      </IconButton>

                      {showMoreMenu && (
                        <div className="more-menu">
                          {isOwner ? (
                            <button onClick={() => { 
                              setShowMoreMenu(false); 
                              deleteTrippin(); 
                            }}>
                              Delete Trippin
                            </button>
                          ) : (
                            <button onClick={() => { 
                              setShowMoreMenu(false); 
                              openReportDialog("trippin", trippinPost.trippin_post_id);
                            }}>
                              Report
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="image-action-icons">
                {!isMyTrippinView && (
                  <>
                    <IconButton
                      onClick={() =>
                        togglePlaceSave(
                          placeIndex,
                          place.place_id,
                          place.post_place_ids
                        )
                      }
                      size="large"
                      style={{ color: "#fff" }}
                    >
                      {placesState[placeIndex]?.isSaved ? (
                        <BookmarkRounded
                          style={{
                            color: "#2c3e50",
                            width: "33px",
                            height: "33px",
                            filter: "drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.4))",
                          }}
                        />
                      ) : (
                        <BookmarkBorderRounded
                          style={{
                            width: "33px",
                            height: "33px",
                            filter: "drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.4))",
                          }}
                        />
                      )}
                    </IconButton>
                    <IconButton
                      onClick={() =>
                        openPlaceComments(place.post_place_ids[0])
                      }
                      size="large"
                      className="custom-icon-button"
                    >
                      <img src="/assets/icons/comments.svg" alt="Comments" className="custom-icon" />
                    </IconButton>
                    <IconButton
                      onClick={() => sharePlace(place.place_id)}
                      size="large"
                      style={{ color: "#fff" }}
                    >
                      <IosShare
                        style={{
                          width: "33px",
                          height: "33px",
                          filter: "drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.4))",
                        }}
                      />
                    </IconButton>
                  </>
                )}
                <IconButton
                  onClick={() => openPlaceInMaps(place)}
                  size="large"
                  style={{ color: "#fff" }}
                >
                  <Place
                    style={{
                      width: "33px",
                      height: "33px",
                      filter: "drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.4))",
                    }}
                  />
                </IconButton>
              </div>

              {isCommentsOpen && selectedPlaceId && (
                <div className="swiper-no-swiping">
                  <Comments
                    trippinPostId={trippinPost.trippin_post_id}
                    placeName={place.place_name}
                    id={selectedPlaceId}
                    commentType="trippin"
                    onClose={closePlaceComments}
                  />
                </div>
              )}

              <div className="horizontal-swiper-container">
                <Swiper
                  direction="horizontal"
                  modules={[Mousewheel]}
                  mousewheel={false}
                  nested={true}
                  className="swiper-horizontal"
                  onSlideChange={handleHorizontalSlideChange}
                >
                  {Array.isArray(place.images) && place.images.length > 0 ? (
                    place.images.map((image, imgIndex) => {   
                      const isVideo = isVideoFile(image);
                      return (
                        <SwiperSlide key={imgIndex}>
                          {isVideo ? (
                            <video
                              src={image}
                              className="place-image"
                              loop
                              playsInline
                              // style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <img
                              src={image}
                              alt={`Place ${placeIndex} - Photo ${imgIndex}`}
                              className="place-image"
                              // style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          )}
                        </SwiperSlide>
                      );
                    })
                  ) : (
                    <SwiperSlide>
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          background: "#ccc",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        No images
                      </div>
                    </SwiperSlide>
                  )}
                </Swiper>
              </div>

              <div
                className="caption-area"
                onTouchStart={(e) => { 
                  //e.stopPropagation(); 
                  handleTouchStart(e); 
                }}
                onTouchEnd={(e) => { 
                  //e.stopPropagation();
                  handleTouchEnd(e); 
                }}
                onMouseDown={(e) => { 
                  //e.stopPropagation(); 
                  handleTouchStart(e); 
                }}
                onMouseUp={(e) => { 
                  //e.stopPropagation(); 
                  handleTouchEnd(e); 
                }}
              >
                
                <h2 className="place-title">{place.place_name}</h2>
                {place.caption && <p className="caption">{place.caption}</p>}

                {place.review && (
                  <div className="review-rating">
                    {Array.from({ length: 5 }).map((_, index) =>
                      index < place.review ? (
                        <Star key={index} style={{ color: "#FFD700" }} />
                      ) : (
                        <StarBorder key={index} style={{ color: "#FFD700" }} />
                      )
                    )}
                  </div>
                )}

                {place.price && (
                  <p className="price">
                    <SellRounded className="money-icon" />
                    <span>${place.price}</span>
                  </p>
                )}

                {place.companionship && (
                  <p className="companionship">
                    <strong>Companionship:</strong> {capitalize(place.companionship)}
                  </p>
                )}

                <div className="trippin-icons">
                  {!isMyTrippinView && (
                    <>
                      <IconButton onClick={toggleTrippinLike}>
                        {isTrippinLiked ? <FavoriteRounded style={{ color: "#FF3040" }}/> : <FavoriteBorderRounded />}
                      </IconButton>
                      <IconButton onClick={toggleTrippinSave}>
                        {isTrippinSaved ? <BookmarkRounded style={{ color: "#2c3e50" }}/> : <BookmarkBorderRounded />}
                      </IconButton>
                      <IconButton onClick={shareTrippin}>
                        <IosShare />
                      </IconButton>
                    </>
                  )}
                  <IconButton onClick={openTrippinInMaps}>
                    <Map />
                  </IconButton>
                  {allowTagging && (
                    <IconButton
                      onClick={() => {
                        setSelectedPlaceIdForTagging(place.place_id);
                        setIsPlaceTagDialogOpen(true);
                      }}
                    >
                      <StyleIcon />
                    </IconButton>
                  )}
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};